import React from 'react';
import moment from 'moment';
import { __ } from 'i18n/index';

import { MatchGame } from 'models/MatchGame.jsx';
import { Stage } from 'models/Stage.jsx';
import { Request } from 'components/Request.jsx';
import { BrowserNotification } from 'components/BrowserNotification.jsx';
import { MatchSounds } from 'components/MatchSounds.jsx';
import { Dashboard } from 'components/Dashboard.jsx';
import { GameRoom } from './GameRoom.jsx';
import { DateFormat } from 'components/DateFormat.jsx';
import { League } from './League.jsx';
import { Season } from './Season.jsx';
import { ChatMessages } from 'models/ChatMessages.jsx';
import { ChatMessage, ChatMessageNotification } from 'models/ChatMessage.jsx';
import { matchSearchListManager } from 'components/MatchSearchListManager.jsx';

import { MatchHostCode } from 'models/MatchHostCode.jsx';
import { MatchPlayer } from 'models/MatchPlayer.jsx';
import { MatchDolphinStatus } from 'models/MatchDolphinStatus.jsx';


import { Settings } from 'components/Settings.jsx';
import MatchSearchFilterReasons from '../components/MatchSearchFilterReasons.jsx';
import Infraction from './Infraction.jsx';
import MatchTeam from './MatchTeam.jsx';
import { UserComponent } from '../react/UserComponent.jsx';
import { returnToMatchView } from 'react/redux/actions/matchmaking.js';
import { CacheableDataObject } from './CacheableDataObject.jsx';
import MatchTeamReport from './MatchTeamReport.js';
import { openedMatchPopup } from 'react/redux/actions/matchmaking.js';
import { closedMatchPopup } from 'react/redux/actions/matchmaking.js';
import { LadderPopup } from '../components/LadderPopup.jsx';

import store from 'react/redux/store/store.js';
import * as matchmakingActions from 'react/redux/actions/matchmaking.js';
import TierRestriction from "./TierRestriction";
import PickSearchesComponent from "../react/matchmaking/PickSearchesComponent.jsx";
import { openPage } from 'navigation/pageLocation';
import { showToast } from 'react/toasts/showToast';

class Match extends CacheableDataObject {
	static CUSTOM_QUEUE_ONLY = 0;
	static AUTO_QUEUE_ONLY = 1;
	static AUTO_QUEUE_AND_CUSTOM_QUEUE = 2;

	// Mirrors Ladder_Match::RESTART_THRESHOLD_SECONDS — host can only restart the
	// search timer once the remaining time has fallen below this threshold.
	static RESTART_THRESHOLD_SECONDS = 15 * 60;

	beforeConstruct() {
		this.hadChatPopulated = false;
		this.players = [];

		this.accepted_players = new Map();
		this.is_removed = false;
		this.host_code = MatchHostCode.create({
			match: this
		});

		this.failReasons = new MatchSearchFilterReasons();
		this.moderation = { requested_at: null, moderators: [] };
		this.getSearchTimeRemaining = this.getSearchTimeRemaining.bind(this); // Used in display functions so it gets bound here
	}

	afterConstruct() {
		this.chatMessages = ChatMessages.create();
		this.chatMessages.setMatch(this);

		if(this.isDoublesInvitation()) {
			this.parent_match_id = this.id;
		}
	}

	afterUpdate() {
		if(this.player1 === this.player2 && this.isDoubles()) {
			//Workaround for situation where a player is invited to a doubles match
			delete this.player1;
		}
	}

	get location() {
		const mainPlayer = this.mainPlayer;
		if(!mainPlayer) {
			return null;
		}
		return mainPlayer.location;
	}

	get gameData() {
		return Dashboard.getDataForLadder(this.ladder ? this.ladder.id : null);
	}

	searchIsVisible() {
		return !this.failReasons.hasFailReasons();
	}

	isStarted() {
		return this.search_time_remaining === null;
	}

	get isSearch() {
		if(this.player1 && !this.player2 && Number.parseInt(this.search_time_remaining, 10)) {
			return true;
		}
		if(this.isDoublesInvitation()) {
			return true;
		}
		return false;
	}

	isDoublesInvitation() {
		return this.isDoubles() && !this.player1 && this.player2;
	}

	isPendingReply() {
		return (!(this.isSearch && !this.isDoublesInvitation()));
	}

	replyWasSentByMe() {
		if(this.player1 && !this.player1.isMe()) {
			return true;
		}
		return false;
	}

	get mainPlayer() {
		if(this.isSearch) {
			if(this.isSingles()) {
				return this.player1;
			}
			else {
				if(this.player1) {
					return this.player1;
				}
				//Is an invite, which for some reason has no player1
				return this.player2;
			}
		}
		else if(this.player2) {
			if(this.isMySearch()) {
				return this.player2;
			}
			else {
				return this.player1;
			}
		}
		else {
			if(this.player1) {
				return this.player1;
			}
			if(this.player2) {
				return this.player2;
			}
		}
		return null;

	}

	setPerspectivePlayer(player) {
		this.thePerspectivePlayer = player;
	}

	get perspectivePlayer() {
		return this.thePerspectivePlayer || myUser;
	}

	get duration() {
		if(this.start_time && this.end_time) {
			const duration = moment.duration(this.start_time.diff(this.end_time));
			return duration.humanize();
		}
	}

	set duration(duration) {
		console.error('deprecated');
	}

	getLeagueInformationForPlayer(player) {
		const leagueProps = {};
		const matchPlayer = this.players[player.id];
		if(this.isRanked()) {
			if(matchPlayer && matchPlayer.match && matchPlayer.match.league) {
				leagueProps.league = matchPlayer.match.league;
			}
		}
		else {
			if(matchPlayer && matchPlayer.match && matchPlayer.match.highest_league) {
				leagueProps.league = matchPlayer.match.highest_league;

				if(leagueProps.league) {
					leagueProps.title = `Highest Rank Achieved ${leagueProps.league.name ? ` - ${leagueProps.league.name}` : ''} ${leagueProps.league.when ? ' - ' + leagueProps.league.when.format('LLL') : ''}`;
					leagueProps.requirementsNecessaryToBeDisplayed = false;
					leagueProps.hidePoints = true;
				}
			}
		}
		return leagueProps;
	}

	startTime() {
		const timeToUse = this.start_time || this.created_at;
		if(!timeToUse) {
			console.error('hmm');
			console.log(this);
		}
		return timeToUse || null;
	}

	startTimeCalendar() {
		const timeToUse = this.startTime();
		if(moment.isMoment(timeToUse)) {
			return timeToUse.calendar(null, {
					sameElse: function(now) {
						if(this.isSame(now, 'year')) {
							return 'MMM Do';
						}
						else {
							return 'MMM Do, \'YY';
						}
					},
				}
			);
		}
		else {
			return '';
		}
	}

	getTypeName() {
		return this.is_ranked ? __('Ranked') : __('Friendlies');
	}

	getTypeClass() {
		return this.is_ranked ? 'is_ranked' : 'friendlies';
	}

	isRanked() {
		return this.is_ranked;
	}

	getUrl() {
		return siteUrl + '/match/view/' + this.id;
	}


	getTeamSizeName() {
		if(this.team_size == 1) {
			return __('Singles');
		}
		else {
			return __('Doubles');
		}
	}

	/**
	 * This function is used a lot for visual things, so it will return 1 if the current user is not on a team
	 * @returns {number}
	 */
	getMyTeamNumber() {
		const teamNumber = this.getPlayerTeamNumber(myUser);
		return teamNumber || 1;
	}

	getPlayerTeamNumber(player) {
		if(!player || !player.id) {
			return null;
		}
		if(!this.players) {
			console.log('No players O_O?', this);
			return null;
		}
		if(!this.players[player.id]) {
			return null;
		}
		if(this.players[player.id].match && !this.players[player.id].match.team_number) {
			return null;
		}
		if(!this.players[player.id].match) {
			return null;
		}
		return this.players[player.id].match.team_number;
	}

	hasWinner() {
		return this.final_result === 1 || this.final_result === 2;
	}

	endedNeutrally() {
		return this.final_result === 4;
	}

	playerHasResult(player) {
		const teamNumber = this.getPlayerTeamNumber(player);
		return !!teamNumber && this.hasWinner();
	}

	playerWon(player) {
		const teamNumber = this.getPlayerTeamNumber(player);
		return this.final_result === teamNumber;
	}

	playerLost(player) {
		const teamNumber = this.getPlayerTeamNumber(player);
		return this.final_result === teamNumber;
	}

	getConnectedPlayersAsATeam() {
		return [
			MatchTeam.create(
				{
					match: this,
					number: 3,
					players: Array.from(this.connected_players).map(([_, player]) => Users.update(player))
				}
			)
		];
	}

	getTeam(teamNumber) {
		return this.getTeams()[teamNumber];
	}

	getTeams() {
		const teams = {};
		_.each(this.players, (player) => {
			if(!player.match || !player.match.team_number) {
				return;
			}
			if(!teams[player.match.team_number]) {
				teams[player.match.team_number] = MatchTeam.create({ match: this });
				teams[player.match.team_number].players = [];
				teams[player.match.team_number].number = player.match.team_number;
			}
			teams[player.match.team_number].players.push(player.player);
		});
		return teams;
	}

	getTeamList() {
		const teams = this.getTeams();
		const teamList = [];
		const teamNumbers = [1, 2];
		for(let teamNumber of teamNumbers) {
			if(!teams[teamNumber]) {
				continue;
			}
			teamList.push(teams[teamNumber]);
		}
		return teamList;
	}

	getTeamsPrioritizeMineFirst(playerToUse) {
		let teamNumber = this.getPlayerTeamNumber(playerToUse);
		if(!teamNumber) {
			teamNumber = this.getMyTeamNumber();
		}
		const teamOrder = teamNumber === 1 ? [1, 2] : [2, 1];
		const teams = this.getTeams();
		const teamsSorted = [];
		for(let teamNumber of teamOrder) {
			if(!teams[teamNumber]) {
				continue;
			}
			teamsSorted.push(teams[teamNumber]);
		}
		return teamsSorted;
	}

	getStrikesRemainingCount() {
		var visibleStages = 0;
		if(!this.game || !this.game.all_stages) {
			return null;
		}
		_.each(this.game.all_stages, (stage, id) => {
			if(this.game.visible_stages[id]) {
				visibleStages++;
			}
		});
		if(this.game.strikes_remaining >= visibleStages) {
			return visibleStages - 1;
		}
		else {
			return this.game.strikes_remaining;
		}
	}

	getTotalMatchesName() {
		if(this.match_count === 0) {
			return __('Endless');
		}
		else {
			if(this.match_count > 5) {
				return __('First To %s', Math.floor(this.match_count / 2) + 1);
			}
			else {
				return __('Best of %s', this.match_count);
			}
		}
	}

	hasRules() {
		return this.ladder && this.ladder.rules && (
			this.ladder.rules.items ||
			this.ladder.rules.stocks ||
			this.ladder.rules.time_limit
		);
	}

	isASet() {
		return this.match_count > 0;
	}

	hasANewAccount() {
		let hasANew = false;
		_.each(this.players, (matchPlayer) => {
			const player = matchPlayer.player;
			if(player && player.is_a_new_account) {
				hasANew = true;
			}
		});
		return hasANew;
	}

	getMyPlayer() {
		if(!this.players) {
			return null;
		}
		return this.players[myUser.id];
	}

	hasSameBasicSetup(otherMatch) {
		if(otherMatch.ladder_id !== this.ladder_id) {
			return false;
		}
		if(otherMatch.match_count !== this.match_count) {
			return false;
		}
		if(otherMatch.is_ranked !== this.is_ranked) {
			return false;
		}
		if(otherMatch.team_size !== this.team_size) {
			return false;
		}
		return true;
	}

	requiresResponseFromMe() {
		return this.containsMeAsPlayer() && this.isPendingReply() && !this.replyWasSentByMe() && this.accepted_players.size === 0;
	}

	containsMeAsPlayer() {
		// Gonna assume there was a good reason for not using containsPlayer()
		if(!this.players) {
			return false;
		}
		return !!this.players[myUser.id];
	}

	containsPlayer(player) {
		if(this.player1 && this.player1.id === player.id || this.player2 && this.player2.id === player.id) {
			return true;
		}
		if(this.players) {
			return !!this.players[player.id];
		}
		return false;
	}

	getWinsRequired() {
		if(this.match_count > 0) {
			return (Math.floor(this.match_count / 2) + 1);
		}
		else {
			return null;
		}
	}

	toString() {
		return `Match - ${this.id}`;
	}

	getMyTeamWinCount() {
		return this.getMyTeamNumber() == 1 ? this.set_score.p1wins : this.set_score.p2wins;
	}

	getOtherTeamWinCount() {
		return this.getMyTeamNumber() == 1 ? this.set_score.p2wins : this.set_score.p1wins;
	}

	getCurrentGameNumber() {
		return this.rps_game ? this.rps_game.game_number : this.game.game_number;
	}

	getOtherTeamNumber() {
		var otherTeamNumber = this.getMyTeamNumber();
		if(otherTeamNumber === null) {
			return 2;
		}
		return otherTeamNumber == 1 ? 2 : 1;
	}

	getPlayers() {
		var playerList = [];
		if(this.players) //Primary list
		{
			for(let i in this.players) {
				if(!this.players.hasOwnProperty(i)) {
					continue;
				}
				let player = this.players[i];
				player = player.player;
				if(player) {
					playerList.push(player);
				}
			}
		}
		return playerList;
	};

	isAutoReportedOnly() {
		return this.dolphin_status && this.match_count == 0;
	}

	isAutoQueued() {
		return this.queue_type >= 1;
	}

	isAutoQueuedOnly() {
		return this.queue_type === Match.AUTO_QUEUE_ONLY;
	}

	isBothQueued() {
		return this.queue_type === Match.AUTO_QUEUE_AND_CUSTOM_QUEUE;
	}

	isEnded() {
		return this.end_phase || this.is_disputed || (this.game && this.game.current_action === null);
	}

	hasHost(player) {
		if(!player) {
			return false;
		}
		if(player === this.player1) {
			return true;
		}
		return false;
	}

	getMatchClosedMessage() {
		const otherPlayers = this.getOtherPlayers();
		if(this.isSingles()) {
			return (
				<span>
					<UserComponent
						user={otherPlayers[0]}
					/>
					{' '}
					has left!
				</span>
			);
		}
		else {
			return null;
		}
	}

	disputeIsActive() {
		return !!this.is_disputed && !this.is_completed;
	}

	shouldShowHostCodePopup() {
		if(this.cachedShouldShowHostCodePopup !== undefined) {
			return this.cachedShouldShowHostCodePopup;
		}
		const mySearchPlayer = this.getMyPlayer();
		if(!mySearchPlayer) {
			return this.cachedShouldShowHostCodePopup = false;
		}

		return this.cachedShouldShowHostCodePopup = Settings.isChecked('show_host_code_popup_user_entry') &&
			this.mainPlayer &&
			this.mainPlayer.isMe() &&
			!this.shouldViewLobby() &&
			this.ladder.has_host_code &&
			mySearchPlayer &&
			mySearchPlayer.match;
	}

	usesDolphinHostCodes() {
		return this.ladder.has_host_code;
	}

	usesGameCodes() {
		return this.usesDolphinHostCodes() || this.usesArenaCodes();
	}

	usesArenaCodes() {
		return this.ladder.game_system === 8;
	}

	shouldViewLobby() {
		if(this.containsPlayerInLobby(myUser)) {
			return true;
		}
		if(this.players && this.players[myUser.id] && this.players[myUser.id].match && this.players[myUser.id].match.still_chatting) {
			return true;
		}
		return false;
	}


	// A challenge has no title of its own, so the server sends the title of the search it
	// answers. On a challenge to my search that is my own message, not the challenger's.
	get listingTitle() {
		if(this.parent_match_id && this.player2 && this.isMySearch()) {
			return null;
		}
		return this.title;
	}

	isMySearch() {
		if(!this.player1) {
			return;
		}
		return this.player1.isMe();
	}

	static isInMatchWith(otherPlayer) {
		const match = matchSearchListManager.getCurrentMatch();
		if(match) {
			return match.containsPlayerInLobby(otherPlayer);
		}
		return false;
	}

	getDistance() {
		if(this.location && this.location.distanceFromUser) {
			return this.location.distanceFromUser.distance;
		}
		return 999999999;
	}

	getSearchTimeRemaining() {
		if(this.searchTimeEnd === true) {
			return true;
		}
		else {
			return this.searchTimeEnd.diff(moment(), 'seconds');
		}
	}

	canRestartSearch() {
		const remaining = this.getSearchTimeRemaining();
		if(remaining === true) {
			return false;
		}
		return remaining <= Match.RESTART_THRESHOLD_SECONDS;
	}

	secondsUntilRestartAllowed() {
		const remaining = this.getSearchTimeRemaining();
		if(remaining === true) {
			return 0;
		}
		return Math.max(0, remaining - Match.RESTART_THRESHOLD_SECONDS);
	}

	postNotification(message, callback) {
		if(this.chatMessages) {
			if(typeof (message) === 'string' || message instanceof String) {
				const notification = ChatMessageNotification.create();
				notification.summary = message;
				message = notification;
			}

			message.match = this;
			this.chatMessages.addNotification(message);
		}
		else {
			console.log(this);
			console.log('Could not post notification:', message);
		}
	}

	chatMessagesAndEvents(withEvents = false) {
		const extraMessages = [];
		if(withEvents && this.match_events) {
			for(let event of this.match_events) {
				const notification = ChatMessageNotification.create(event);
				notification.type = event.type;
				notification.match = this;
				extraMessages.push(notification);
			}
			this.chatMessages.extraMessagesToInject = extraMessages;
		}
		else {
			this.chatMessages.extraMessagesToInject = null;
		}

		return this.chatMessages;
	}

	showWantsToPlayNotification(notification) {
		if(!notification) {
			return;
		}
		BrowserNotification.showNotification(notification.title, notification, true);
		MatchSounds.playMatchRequestNotification();
	}

	showAutoQueueMatchFoundNotification() {
		let icon = undefined;
		if(this.ladder && this.ladder.small_image) {
			icon = this.ladder.small_image;
		}
		const notificationData =
			{
				body: `${this.getTotalMatchesName()} ${this.getTeamSizeName()}`,
				icon: icon,
				onClick: function() {
					store.dispatch((returnToMatchView()));
				}
			};
		BrowserNotification.showNotification(`Auto Queue Matchup Found for ${this.ladder.name}!`, notificationData, true);
		MatchSounds.playMatchRequestNotification();
	}

	showMatchStartedNotification() {
		const otherPlayers = this.getOtherPlayers();
		// if(!BrowserNotification.browserHasFocus)
		{
			MatchSounds.playJingles();
		}
		const playerNames = otherPlayers.map((player) => {
			return player.displayedName;
		});
		if(playerNames.length) {
			this.browserNotification('Match Started!',
				'With ' + playerNames.join(', '), false);
		}
	}

	showSimilarSearchBrowserNotification() {

		var title = this.player1.username;
		const placeName = this.player1.location.relativeLocation();
		var locationInformation = placeName ? ' (' + placeName + ')' : '';

		var matchInformation = ` started a ${this.ladder.name} search`;


		title = `${title} ${locationInformation} ${matchInformation}`;


		var inChatNotification = this.browserNotification(title,
			`${this.getTeamSizeName()} ${this.getTotalMatchesName()} ${this.getTypeName()}`, false);
		// if(inChatNotification)
		// {
		//     inChatNotification.remove();
		// }
		// MatchSounds.playStickiedMatch();
	};

	report(result, disputeMessage) {
		if(result === CANCEL_MATCH) {
			return new Promise((resolve, reject) => {
				let submittingResult = false;
				LadderPopup.create('Canceling means you hope for no moderator intervention, if both players select this the match will be nullified!', 'Cancel Match', {
					buttons: [
						{
							text: 'Nevermind',
							dismiss: true,
						},
						{
							text: 'Yes Cancel!',
							dismiss: true,
							click: () => {
								submittingResult = true;
								this._submitReport(result, disputeMessage)
									.then(() => {
										resolve();
									});

							}
						}
					]
				}).onDismiss.then(() => {
					if(!submittingResult) {
						reject('Agree to cancel, Cancelled!');
					}
				});
			});
		}
		else {
			return this._submitReport(result, disputeMessage);
		}
	}

	// Doubles: the team's cancel request is a toggle either teammate can
	// press, so sending Cancel again withdraws it. No confirmation popup.
	takeBackCancelRequest() {
		return this._submitReport(CANCEL_MATCH);
	}

	_submitReport(result, disputeMessage) {
		const data = {
			won: result,
			message: disputeMessage,
			match_id: this.id
		};
		return Request.apiv1('match/report', data)
			.then(function(response) {

				if(response.success && response.error) {
					showToast({ kind: 'warning', content: response.error });
				}
				Dashboard.performOpenSearchUpdate(response);
			})
			.catch(function() {
				showToast({ kind: 'warning', content: __('Your report did not reach the server. Try again.') });
			});
		addGaEvent('matchmaking', 'report-match');
	}

	get ladder_id() {
		return this.ladder.id;
	}

	set ladder_id(empty) {
		console.error('should be unused');
	}

}

Match.prototype.dataLocationParsers = {
	id: (match, data) => {
		match.id = Number.parseInt(data.id);
	},
	is_ranked: (match, data) => {
		match.is_ranked = !!data.is_ranked;
	},
	infractions: (match, data) => {
		match.infractions = Infraction.convert(data.infractions);
	},
	// Players only: when someone asked for a moderator and the mods who joined
	// the match chat (react/match/moderation/matchModeration).
	moderation: (match, data) => {
		const moderation = data.moderation || {};
		match.moderation = {
			requested_at: moderation.requested_at ? Number(moderation.requested_at) : null,
			moderators: (moderation.moderators || []).map((moderator) => ({
				id: Number(moderator.id),
				username: moderator.username,
				joined: !!moderator.joined,
			})),
		};
	},
	chat: (match, data) => {
		let newMessagesAdded = null;
		match.hadChatPopulated = true;
		if(!data.chat || !data.chat.chat_messages) {
			newMessagesAdded = [];
		}
		else {
			newMessagesAdded = match.chatMessages.addMessages({
				messagesAreNew: true,
				messages: data.chat.chat_messages
			});
		}
		newMessagesAdded = newMessagesAdded.length ? newMessagesAdded[0] : null;

		const myCurrentMatch = matchSearchListManager.getCurrentMatch();
		if(!myCurrentMatch || myCurrentMatch.id !== match.id) {
			return;
		}

		if(newMessagesAdded && !newMessagesAdded.player.isMe() && !newMessagesAdded.isStateMessage()) {
			MatchSounds.playPrivateMessageSoundEffect();
			if(!BrowserNotification.checkBrowserFocus()) {
				BrowserNotification.titleNotification('Received a message on your match with ' + newMessagesAdded.player.username + '!', 0, 2);
			}

			var icon = null;
			if(newMessagesAdded.player.selected_flair) {
				icon = newMessagesAdded.player.selected_flair.fullUrl;
			}
			else if(match.ladder && match.ladder.small_image) {
				icon = match.ladder.small_image;
			}
			else {
				icon = undefined;
			}
			BrowserNotification.showNotification('Match message from ' + newMessagesAdded.player.username,
				{
					body: newMessagesAdded.message,
					icon: icon,
					onClick: function() {
						store.dispatch((returnToMatchView()));
					}
				});
			const chatNotification = ChatMessageNotification.create();

			chatNotification.player = newMessagesAdded.player;
			chatNotification.type = ChatMessageNotification.types.MATCH_MESSAGE;
			chatNotification.summary = newMessagesAdded.message;

			if(!Dashboard.battlePaneIsVisible()) {
				chatNotification.postToMainChat();
			}

		}
	},
	player_report: (match, data) => {
		if(!data.player_report) {
			match.player_report = null;
			return;
		}
		const teams = match.getTeams();
		match.player_report = {};
		for(let teamNumber in data.player_report) {
			if(!data.player_report.hasOwnProperty(teamNumber)) {
				continue;
			}

			match.player_report[teamNumber] = MatchTeamReport.create();
			match.player_report[teamNumber].update({
				...data.player_report[teamNumber],
				team: teams[teamNumber],
			});
		}
	},
	dolphin_status: (match, data) => {
		if(!match.dolphin_status) {
			match.dolphin_status = MatchDolphinStatus.create();
		}
		match.dolphin_status.update(data.dolphin_status);
	},
	search_time_remaining: (match, data) => {
		match.search_time_remaining = data.search_time_remaining;
		if(data.search_time_remaining === true) {
			match.searchTimeStart = true;
			match.searchTimeEnd = true;
		}
		else {
			match.searchTimeStart = moment.unix(data.created_at);
			match.searchTimeEnd = moment().add(data.search_time_remaining, 'seconds');
		}
	},
	created_at: (match, data) => {
		match.created_at = DateFormat.momentFromLadderTime(data.created_at);
	},
	start_time: (match, data) => {
		match.start_time = DateFormat.momentFromLadderTime(data.start_time);
	},
	end_time: (match, data) => {
		match.end_time = DateFormat.momentFromLadderTime(data.end_time);
	},
	host_code: (match, data) => {
		match.host_code = match.host_code.updateHostCode(data.host_code, match);
	},
	accepted_players: (match, data) => {
		match.accepted_players = Users.convertCollection(data.accepted_players);
	},
	connected_players: (match, data) => {
		match.connected_players = Users.convertCollection(data.connected_players);
	},
	match_muted: function(match, data) {
		if(!match.match_muted) {
			match.match_muted = {};
		}
		if(data.match_muted.responses) {
			match.match_muted.responses = [];
			for(let i in data.match_muted.responses) {
				if(!data.match_muted.responses.hasOwnProperty(i)) {
					return;
				}
				match.match_muted.responses.push(data.match_muted.responses[i]);
			}
		}
		if(data.match_muted.until) {
			match.match_muted.until = DateFormat.momentFromLadderTime(data.match_muted.until);
		}
	},
	ladder: function(match, data) {
		match.ladder = GameRoom.retrieve(data.ladder);
	},
	lobby(match, data) {
		if(!data.lobby) {
			return;
		}
		match.lobby = {
			total_players: data.lobby.total_players,
			required_players: data.lobby.required_players,
		};
		if(data.lobby.players) {
			match.lobby.players = {};
			for(let i in data.lobby.players) {
				match.lobby.players[i] = Users.update(data.lobby.players[i]);
			}
		}
		if(data.lobby.invited) {
			match.lobby.invited = {};
			for(let i in data.lobby.invited) {
				match.lobby.invited[i] = Users.update(data.lobby.invited[i]);
			}
		}
	},
	season: function(match, data) {
		match.season = Season.retrieve(data.season);
	},
	previous_game: function(match, data) {
		match.previous_game = MatchGame.create({ ...data.previous_game, match });
	},
	game(match, data) {
		match.game = MatchGame.create({ ...data.game, match });
	},
	set_games(match, data) {
		match.set_games = data.set_games.map((game) => MatchGame.create({ ...game, match }));
	},
	games(match, data) {
		match.games = data.games.map((game) => {
			return MatchGame.create({ ...game, match });
		});
	},
	stages: function(match, data) {
		if(!match.stages) {
			match.stages = [];
		}
		for(let i = 0; i < data.stages.length; i++) {
			match.stages[i] = Stage.retrieve(data.stages[i]);
		}
	},
	tier_restriction: function(match, data) {
		match.tier_restriction = TierRestriction.create(data.tier_restriction);
	},
	parent_match_id: function(match, data) {
		match.parent_match_id = data.parent_match_id;
	},
	player1: function(match, data) {
		match.player1 = Users.update(data.player1);
	},
	player2: function(match, data) {
		match.player2 = Users.update(data.player2);
	},
	players: function(match, data) {
		match.players = {};
		for(let id in data.players) {
			if(!data.players.hasOwnProperty(id)) {
				continue;
			}
			const player = data.players[id];
			match.players[id] = new MatchPlayer(player);
		}
	},
};

Match.prototype.getOtherPlayers = function() {
	var playerList = [];
	if(this.isSingles()) {
		if(this.player1 && this.player1.id != this.perspectivePlayer.id) {
			playerList.push(this.player1);
		}
		if(this.player2 && this.player2.id != this.perspectivePlayer.id) {
			playerList.push(this.player2);
		}
		return playerList;
	}
	if(this.players) //Primary list
	{
		$.each(this.players, (i, player) => {
			player = player.player;
			if(!player) {
				return;
			}
			if(player.id != this.perspectivePlayer.id) {
				playerList.push(player);
			}
		});
		return playerList;
	}
	if(this.lobby) {
		$.each(this.lobby.players, (i, player) => {
			if(player.id != this.perspectivePlayer.id) {
				playerList.push(player);
			}
		});
		return playerList;
	}
	return [];
};
Match.prototype.containsPlayerInLobby = function(player) {
	if(this.lobby && this.lobby.players) {
		return !!this.lobby.players[player.id];
	}
	return false;
};
Match.prototype.getMatchPlayerCount = function() {
	var count = 0;
	$.each(this.players, function(i, player) {
		if(player.match && player.match.still_chatting) {
			count++;
		}
	});
	return count;
};
Match.prototype.atLeastOneOtherPlayerIsChatting = function() {
	return this.getMatchPlayerCount() > 1;
};
Match.prototype.isSingles = function() {
	return this.team_size == 1;
};
Match.prototype.isDoubles = function() {
	return this.team_size == 2;
};
Match.prototype.usesTeamList = function() {
	return this.isDoubles();
};

Match.prototype.updateDisputeReason = function(message, finishedCallback) {
	var data = { match_id: this.id, dispute_message: message };
	var match = this;
	return Request.post(siteUrl + '/matchmaking/update_dispute_message', data).then(function(result) {
		if(result.success) {
			if(!match.player_report) {
				match.player_report = {};
			}
			if(!match.player_report[match.getMyTeamNumber()]) {
				match.player_report[match.getMyTeamNumber()] = {};
			}
			match.player_report[match.getMyTeamNumber()].dispute_message = message;
		}
		if(finishedCallback) {
			finishedCallback(result);
		}
	}).catch(function() {
		showToast({ kind: 'warning', content: __('There was a server error saving the dispute message') });
	});
};
Match.prototype.closeMatch = async function() {
	const data = { match_id: this.id };
	const matchExitNormal = (result) => {
		if(result.error) {
			showToast({ kind: 'warning', content: result.error });
		}
		else {
			store.dispatch(matchmakingActions.returnToMatchmakingTab());
		}
	};
	if(!this.is_ranked) {
		return Request.apiv1('match/exit', data)
			.then(matchExitNormal);
	}
	if(this.canBeExited()) {
		return Request.apiv1('match/exit', data)
			.then(matchExitNormal);

	}
	else {
		LadderPopup.create('This match is disputed, please fill out a reason so that it can be exited.',
			'Something Must Be Done...', {
				buttons: []
			});
		return Promise.reject();
	}
};
Match.prototype.canBeExited = function() {
	if(!this.is_disputed) {
		return true;
	}
	var match = this;
	if(match.is_cancelled) {
		return true;
	}
	if(match.is_completed) {
		return true;
	}
	if(!match.player_report) {
		return true;
	}
	if(match.player_report && match.player_report[match.getMyTeamNumber()].dispute_message
		&& match.player_report[match.getMyTeamNumber()].dispute_message.length > 0) {
		return true;
	}
	else {
		return false;
	}
};

Match.prototype.browserNotification = function(title, text, showInChat, showTitleNotification) {

	let options = this.getBrowserNotificationOptions();
	options.body = text;
	if(!showTitleNotification) {
		options.showTitleNotification = false;
	}
	const result = BrowserNotification.showNotification(title, options);
	if(showInChat) {
		return result.showInChatAlso(showInChat);
	}
	return result;
};
Match.prototype.getMyCurrentCharacterId = function() {
	if(this.game && this.game.players && this.game.players[myUser.id]) {
		return this.game.players[myUser.id].character;
	}
	return null;
};

Match.prototype.getBrowserNotificationOptions = function() {
	var challengeNotificationOptions = {};
	if(this.ladder && this.ladder.small_image) {
		challengeNotificationOptions.icon = this.ladder.small_image;
	}
	return challengeNotificationOptions;
};

Match.prototype.getMyTeamNameElement = function() {
	if(this.isSingles()) {
		return myUser.createUsernameElement().prop('outerHTML');
	}
	else if(this.isDoubles()) {
		return 'Your Team';
	}
};
Match.prototype.getOtherTeamNameElement = function() {
	if(this.isSingles()) {
		return this.getOtherPlayers()[0].createUsernameElement().prop('outerHTML');
	}
	else if(this.isDoubles()) {
		return 'The Other Team';
	}
};

Match.openMatchOnlyPopup = function(e) {
	e.preventDefault();
	const w = 600;
	const h = 800;
	const left = (screen.width / 2) - (w / 2);
	const top = (screen.height / 2) - (h / 2);
	store.dispatch(openedMatchPopup());
	const newWindow = openPage(
		siteUrl + '/netplay?match_only=true&tab=battle', 'match_popup', 'toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, copyhistory=no, width=' + w + ', height=' + h + ', top=' + top + ', left=' + left);
	newWindow.onbeforeunload = function() {
		store.dispatch(closedMatchPopup());
	};
};

export { Match };